import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Truck, Search, X,
  ChevronLeft, ChevronRight, RefreshCw, Download, Info,
} from 'lucide-react';
import {
  fetchUpsBills, fetchUpsShippingMargin, fetchUpsShippingMarginSummary,
} from '../services/api';

// ── UPS billed vs what we charged the customer for shipping ───────────────────
// Draft report (under Reports) — for review. "What we charge the customer" is
// the shipping line on the order (orders.oshipcost). UPS billed is the net of
// every charge line for that tracking # on an imported UPS bill. Surcharges
// like Saturday delivery / AK-HI aren't folded in yet — flag if you want them.

const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSigned = n => (Number(n) > 0 ? '+' : '') + fmt(n);
// DATE columns serialize at UTC midnight — new Date() shows the previous day
// in Eastern time. Parse the date part as local (same fix as UpsBills.js).
const fmtDate = v => {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const STATUS = {
  profit:    { label: 'Profit',     color: '#1a8a4a',         bg: '#e6f4ea', blurb: 'We charged the customer more than UPS billed us' },
  loss:      { label: 'Loss',       color: '#c62828',         bg: '#fde8e8', blurb: 'We charged the customer less than UPS billed us' },
  even:      { label: 'Break-even', color: 'var(--gray-500)', bg: '#eef0f2', blurb: 'Customer charge ≈ UPS bill (within a penny)' },
  free_ship: { label: 'Free ship',  color: 'var(--orange)',   bg: '#fff1e8', blurb: 'Customer charged $0 — VIP / free-ship promo. We absorb the whole UPS cost.' },
  no_charge: { label: 'No charge',  color: 'var(--gray-500)', bg: '#f4f5f6', blurb: 'Order matched but no shipping charge recorded on it' },
  no_order:  { label: 'No order',   color: '#5b6470',         bg: '#eceff1', blurb: 'UPS billed this tracking #, but no matching order is in our database' },
};
const STATUS_ORDER = ['profit', 'loss', 'even', 'free_ship', 'no_charge', 'no_order'];
const COMPARABLE = ['profit', 'loss', 'even', 'free_ship'];
const PAGE_SIZE = 250;

function csvCell(v) {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function ShippingMargin({ navigateTo }) {
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState('');   // '' = all bills

  const [summary, setSummary] = useState({ byStatus: {}, total: null });
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Invoice list (once)
  useEffect(() => {
    (async () => {
      setInvLoading(true);
      try {
        const b = await fetchUpsBills();
        setInvoices(Array.isArray(b) ? b : []);
      } catch (e) {
        setError(e.message || 'Could not load UPS bills');
      } finally {
        setInvLoading(false);
      }
    })();
  }, []);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Summary — reloads when the invoice scope changes
  const loadSummary = useCallback(async () => {
    try {
      setSummary(await fetchUpsShippingMarginSummary(selectedInvoice));
    } catch (e) {
      setError(e.message || 'Could not load summary');
    }
  }, [selectedInvoice]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Table rows
  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    setError(null);
    try {
      const r = await fetchUpsShippingMargin(selectedInvoice, {
        status: statusFilter || undefined,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      });
      const list = Array.isArray(r) ? r : [];
      setRows(list);
      setTotalRows(list.length ? Number(list[0].total_rows) || 0 : 0);
    } catch (e) {
      setError(e.message || 'Could not load shipments');
      setRows([]); setTotalRows(0);
    } finally {
      setRowsLoading(false);
    }
  }, [selectedInvoice, statusFilter, debouncedSearch, page]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const onInvoiceChange = (v) => { setSelectedInvoice(v); setStatusFilter(''); setPage(0); };
  const refresh = () => { loadSummary(); loadRows(); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await fetchUpsShippingMargin(selectedInvoice, {
        status: statusFilter || undefined,
        search: debouncedSearch || undefined,
        limit: 5000, offset: 0,
      });
      const list = Array.isArray(all) ? all : [];
      const header = ['Tracking #', 'Order #', 'Customer', 'State', 'Ship Method',
        'Invoice', 'Customer Charged', 'UPS Billed', 'Margin', 'Margin %', 'Status'];
      const lines = [header.join(',')];
      for (const r of list) {
        lines.push([
          r.tracking_number, r.bc_order_id || r.order_id || '',
          r.customer || r.receiver_name || '', r.receiver_state || '',
          r.ship_method || '', r.invoice_number || '',
          r.customer_charged == null ? '' : Number(r.customer_charged).toFixed(2),
          Number(r.ups_billed).toFixed(2),
          r.margin == null ? '' : Number(r.margin).toFixed(2),
          r.margin_pct == null ? '' : Number(r.margin_pct).toFixed(1),
          (STATUS[r.status] || {}).label || r.status,
        ].map(csvCell).join(','));
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipping-margin-${selectedInvoice || 'all-bills'}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const byStatus = summary.byStatus || {};
  const total = summary.total;
  const comp = COMPARABLE.reduce((a, k) => {
    const s = byStatus[k];
    if (s) {
      a.billed  += Number(s.ups_billed || 0);
      a.charged += Number(s.customer_charged || 0);
      a.margin  += Number(s.margin || 0);
      a.n       += Number(s.n || 0);
    }
    return a;
  }, { billed: 0, charged: 0, margin: 0, n: 0 });
  const recovery = comp.billed > 0 ? (comp.charged / comp.billed * 100) : 0;
  const pages = Math.ceil(totalRows / PAGE_SIZE) || 1;
  const showInvoiceCol = selectedInvoice === '';

  const kpis = [
    { label: 'Customer Charged', value: fmt(comp.charged), icon: DollarSign, color: 'var(--navy)',
      sub: `${comp.n.toLocaleString()} comparable shipment${comp.n === 1 ? '' : 's'}` },
    { label: 'UPS Billed', value: fmt(comp.billed), icon: Truck, color: 'var(--navy)',
      sub: 'what UPS charged us for those' },
    { label: 'Net Margin', value: fmtSigned(comp.margin),
      icon: comp.margin >= 0 ? TrendingUp : TrendingDown,
      color: comp.margin >= 0 ? '#1a8a4a' : '#c62828',
      sub: comp.margin >= 0 ? 'shipping covered its cost' : 'we subsidized shipping' },
    { label: 'Cost Recovery', value: `${recovery.toFixed(0)}%`,
      icon: recovery >= 100 ? TrendingUp : TrendingDown,
      color: recovery >= 100 ? '#1a8a4a' : recovery >= 90 ? 'var(--orange)' : '#c62828',
      sub: 'of UPS cost recovered from customers' },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>Shipping Margin</h1>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
          What UPS billed us per shipment vs. what we charged the customer for shipping —
          are we covering our UPS cost? <em>Draft report for review.</em>
        </div>
      </div>

      {invLoading ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
          Loading…
        </div>
      ) : invoices.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
          No UPS bills imported yet. Go to <strong>UPS → Bills</strong> and import a UPS Billing Data
          file — this report draws on that billed data.
        </div>
      ) : (
        <>
          {/* Controls */}
          <div className="card" style={{ marginBottom: 14, padding: '12px 16px', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Bill scope:</span>
              <select
                className="form-select"
                value={selectedInvoice}
                onChange={(e) => onInvoiceChange(e.target.value)}
                style={{ fontSize: 12, padding: '5px 8px', maxWidth: 360 }}
              >
                <option value="">All imported bills</option>
                {invoices.map(b => (
                  <option key={b.invoice_number} value={b.invoice_number}>
                    Invoice {b.invoice_number}
                    {b.invoice_date ? ` · ${fmtDate(b.invoice_date)}` : ''}
                    {b.charge_total != null ? ` · ${fmt(b.charge_total)}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={refresh}>
                <RefreshCw size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Refresh
              </button>
              <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 10px' }}
                disabled={exporting} onClick={exportCsv}>
                <Download size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {exporting ? 'Exporting…' : 'Export CSV'}
              </button>
            </div>
          </div>

          {/* Definition note */}
          <div className="card" style={{ marginBottom: 14, padding: '10px 14px', fontSize: 11.5,
            color: 'var(--navy)', background: '#eef4fb', borderLeft: '3px solid var(--navy)' }}>
            <Info size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            <strong>Customer Charged</strong> is the shipping line on the order. <strong>UPS Billed</strong> is
            every charge line UPS billed for that tracking # (freight + fuel + accessorials + adjustments).
            The headline numbers cover only <strong>comparable</strong> shipments — "No charge" and
            "No order" can't be compared and are excluded.
          </div>

          {error && (
            <div className="card" style={{ marginBottom: 14, padding: '10px 14px', fontSize: 12, color: 'var(--red)' }}>
              {error}
            </div>
          )}

          {/* KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
            {kpis.map(({ label, value, icon: Icon, color, sub }) => (
              <div key={label} className="card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <Icon size={14} style={{ color: 'var(--gray-400)' }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
                </div>
                <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{sub}</div>
              </div>
            ))}
          </div>

          {/* Status panes — click to filter */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginBottom: 14 }}>
            {[
              { key: '', label: 'All', color: 'var(--navy)', bg: '#fff', n: total ? total.n : 0,
                amt: total ? total.ups_billed : 0, signed: false, blurb: 'Every billed shipment — total UPS billed' },
              ...STATUS_ORDER.map(k => {
                const s = byStatus[k] || { n: 0, margin: 0, ups_billed: 0 };
                const isMargin = COMPARABLE.includes(k);
                return {
                  key: k, label: STATUS[k].label, color: STATUS[k].color, bg: STATUS[k].bg,
                  n: s.n, amt: isMargin ? s.margin : s.ups_billed, signed: isMargin, blurb: STATUS[k].blurb,
                };
              }),
            ].map(c => (
              <div
                key={c.key || 'all'}
                onClick={() => { setStatusFilter(c.key); setPage(0); }}
                className="card"
                style={{
                  padding: '10px 12px', cursor: 'pointer', background: c.bg,
                  outline: statusFilter === c.key ? `2px solid ${c.color}` : 'none',
                }}
                title={c.blurb}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{c.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{Number(c.n || 0).toLocaleString()}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.signed ? fmtSigned(c.amt) : fmt(c.amt)}</div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span>
                By shipment
                {statusFilter && <> · filtered to <strong style={{ color: STATUS[statusFilter]?.color }}>{STATUS[statusFilter]?.label}</strong></>}
                <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}> · {totalRows.toLocaleString()} shipment{totalRows === 1 ? '' : 's'}</span>
              </span>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 8, top: 7, color: 'var(--gray-400)' }} />
                <input
                  className="form-input"
                  placeholder="Tracking #, order #, customer…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ fontSize: 12, padding: '5px 8px 5px 26px', width: 250 }}
                />
                {search && (
                  <X size={13} onClick={() => setSearch('')}
                    style={{ position: 'absolute', right: 8, top: 7, color: 'var(--gray-400)', cursor: 'pointer' }} />
                )}
              </div>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {rowsLoading ? (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading…</div>
              ) : rows.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>No shipments match.</div>
              ) : (
                <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Status</th>
                      <th style={{ textAlign: 'left' }}>Tracking #</th>
                      <th style={{ textAlign: 'left' }}>Order</th>
                      <th style={{ textAlign: 'left' }}>Customer</th>
                      <th style={{ textAlign: 'left' }}>Ship Method</th>
                      {showInvoiceCol && <th style={{ textAlign: 'left' }}>Invoice</th>}
                      <th style={{ textAlign: 'right' }}>Customer Charged</th>
                      <th style={{ textAlign: 'right' }}>UPS Billed</th>
                      <th style={{ textAlign: 'right' }}>Margin</th>
                      <th style={{ textAlign: 'right' }}>Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const meta = STATUS[r.status] || STATUS.no_order;
                      const tint = r.status !== 'profit' && r.status !== 'even';
                      const mNum = r.margin == null ? null : Number(r.margin);
                      const orderLabel = r.bc_order_id || r.order_id;
                      return (
                        <tr key={r.tracking_number} style={{ background: tint ? meta.bg : undefined }}>
                          <td>
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 3, color: '#fff', background: meta.color }}>
                              {meta.label}
                            </span>
                          </td>
                          <td style={{ fontFamily: 'monospace' }}>{r.tracking_number}</td>
                          <td>
                            {orderLabel && r.order_id && navigateTo ? (
                              <span style={{ color: 'var(--orange)', fontWeight: 700, cursor: 'pointer' }}
                                onClick={() => navigateTo('order-details', r.order_id)}>
                                {orderLabel}
                              </span>
                            ) : (orderLabel || <span style={{ color: 'var(--gray-400)' }}>—</span>)}
                          </td>
                          <td>
                            {r.customer || r.receiver_name || '—'}
                            {r.receiver_state ? <span style={{ color: 'var(--gray-400)' }}> · {r.receiver_state}</span> : null}
                          </td>
                          <td>{r.ship_method || <span style={{ color: 'var(--gray-400)' }}>—</span>}</td>
                          {showInvoiceCol && (
                            <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--gray-500)' }}>{r.invoice_number || '—'}</td>
                          )}
                          <td style={{ textAlign: 'right' }}>
                            {r.customer_charged == null ? <span style={{ color: 'var(--gray-400)' }}>—</span> : fmt(r.customer_charged)}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(r.ups_billed)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700,
                            color: mNum == null ? 'var(--gray-400)' : mNum > 0 ? '#1a8a4a' : mNum < 0 ? 'var(--red)' : 'var(--gray-500)' }}>
                            {mNum == null ? '—' : fmtSigned(mNum)}
                          </td>
                          <td style={{ textAlign: 'right', color: 'var(--gray-500)' }}>
                            {r.margin_pct == null ? '—' : `${Number(r.margin_pct) > 0 ? '+' : ''}${Number(r.margin_pct).toFixed(1)}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {totalRows > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', fontSize: 12 }}>
                <span style={{ color: 'var(--gray-500)' }}>
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalRows)} of {totalRows.toLocaleString()}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
                    disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
                    <ChevronLeft size={13} /> Prev
                  </button>
                  <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }}
                    disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>
                    Next <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
